import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useManifiestosPorUsuario } from "../../hooks/useManifest";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PublicIcon from "@mui/icons-material/Public";
//import ManifiestoForm from "./ManifestForm";
//import RouteMap from "/rutas/RouteMap";
import RouteMap from "../administracion/rutas/RouteMap";

const ManifestClienteDashboard = () => {
  const navigate = useNavigate();

  // Ver Mapa
  const [openMap, setOpenMap] = useState(false);
  const [openMapID, setOpenMapID] = useState("");

  const { data: manifiestos = [], isLoading, error } = useManifiestosPorUsuario();
  const [mapData, setMapData] = useState({ id: "", vehicleId: null });

  // Estados para la paginación y filtros
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [codigoFilter, setCodigoFilter] = useState("");

  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  };

  const displayedManifiestos = useMemo(() => {
    let filtered = manifiestos;

    // 1. Filtrar por búsqueda de origen/destino y código
    filtered = filtered.filter((manifiesto) => {
      const codigoCompleto = `MN-${String(manifiesto.manid).padStart(6, "0")}`.toLowerCase();
      const codigoRaw = String(manifiesto.manid);

      const matchesSearch =
        search === "" ||
        manifiesto.manori.toLowerCase().includes(search.toLowerCase()) ||
        manifiesto.mandes.toLowerCase().includes(search.toLowerCase());

      const matchesCodigo =
        codigoFilter === "" ||
        codigoCompleto.includes(codigoFilter.toLowerCase()) ||
        codigoRaw.includes(codigoFilter.replace(/^mn-0+/i, ""));

      return matchesSearch && matchesCodigo;
    });

    // 2. Filtrar por pestaña activa
    switch (currentTab) {
      case 0:
        filtered = filtered.filter(manifiesto => manifiesto.manest === 'PLANIFICADO');
        break;
      case 1:
        filtered = filtered.filter(manifiesto => manifiesto.manest === 'EN_PROCESO');
        break;
      case 2:
        filtered = filtered.filter(manifiesto => manifiesto.manest === 'COMPLETADO');
        break;
      default:
        break;
    }

    filtered.sort((a, b) => {
      const dateA = parseDate(a.manfec);
      const dateB = parseDate(b.manfec);

      if (dateA && dateB) {
        return dateB.getTime() - dateA.getTime();
      }
      return 0;
    });

    return filtered;
  }, [manifiestos, search, codigoFilter, currentTab]);

  const filteredManifiestos = manifiestos.filter((manifiesto) => {
    const codigoCompleto = `MN-${String(manifiesto.manid).padStart(6, "0")}`.toLowerCase();
    const codigoRaw = String(manifiesto.manid);

    const matchesStatus = statusFilter ? manifiesto.manest === statusFilter : true;

    const matchesSearch =
      search === "" ||
      manifiesto.manori.toLowerCase().includes(search.toLowerCase()) ||
      manifiesto.mandes.toLowerCase().includes(search.toLowerCase());

    const matchesCodigo =
      codigoFilter === "" ||
      codigoCompleto.includes(codigoFilter.toLowerCase()) ||
      codigoRaw.includes(codigoFilter.replace(/^mn-0+/i, ""));

    return matchesStatus && matchesSearch && matchesCodigo;
  });

  const paginatedManifiestos = displayedManifiestos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatFechaLarga = (dateString) => {
    if (!dateString) return '';

    const fecha = new Date(dateString + 'T00:00:00');
    return new Intl.DateTimeFormat('es-PE', {
      weekday: 'long',  // miércoles
      day: '2-digit',   // 21
      month: 'long',    // mayo
      year: 'numeric'   // 2025
    }).format(fecha);
  };

  const hexToRgba = (hex, alpha = 1) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    if (alpha >= 0 && alpha <= 1) {
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return `rgb(${r}, ${g}, ${b})`;
  };

  const estadoColorMap = {
    CREADO: { label: "CREADO", color: "#90a4ae", text: "#fff" },       // Azul claro ffb300 1976d2
    PLANIFICADO: { label: "PLANIFICADO", color: "#ffb300", text: "#fff" },  // Amarillo/ámbar  ed6c02  ed6c02  ffb300  000
    EN_PROCESO: { label: "EN PROCESO", color: "#4caf50", text: "#fff" },   // Verde 4caf50  ffb300 4caf50
    COMPLETADO: { label: "COMPLETADO", color: "#1e88e5", text: "#fff" },   // Azul oscuro 1976d2
    CANCELADO: { label: "CANCELADO", color: "#e53935", text: "#fff" },    // Rojo
  };

  const getTabSolidColor = (tabIndex) => {
    switch (tabIndex) {
      case 0: return estadoColorMap.PLANIFICADO.color;
      case 1: return estadoColorMap.EN_PROCESO.color;
      case 2: return estadoColorMap.COMPLETADO.color;
      default: return 'transparent';
    }
  };

  const getTabTransparentBackgroundColor = (tabIndex, alpha = 0.25) => {
    const solidColor = getTabSolidColor(tabIndex);
    if (solidColor === 'transparent') {
      return 'transparent';
    }
    return hexToRgba(solidColor, alpha);
  };

  if (isLoading) return <Typography>Cargando manifiestos...</Typography>;
  if (error) return <Typography>Error al cargar manifiestos: {error.message}</Typography>;

  return (
    <Paper sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Manifiestos</Typography>
      </Box>

      {/* Filtros */}
      <Box display="flex" gap={2} mb={2}>
        <TextField
          label="Buscar Origen/Destino"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <TextField
          label="Buscar Código (ej: MN-000045 o 45)"
          fullWidth
          value={codigoFilter}
          onChange={(e) => setCodigoFilter(e.target.value)}
        />
      </Box>

      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        aria-label="manifiesto tabs"
        variant="fullWidth"
        indicatorColor="primary"
        textColor="primary"
        sx={{
          mb: 2,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          '& .MuiTabs-indicator': {
            display: 'none',
            backgroundColor: getTabSolidColor(currentTab),
            height: '3px',
          },
          '& .MuiTab-root': {
            minHeight: 48,
            fontWeight: 'bold',
            fontSize: '0.9rem',
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
              opacity: 1,
            },
            '&.Mui-selected': {
              color: "#000",
              backgroundColor: getTabTransparentBackgroundColor(currentTab, 0.3),
              borderBottom: '3px solid',
              borderColor: getTabSolidColor(currentTab),
            },
          },
        }}
      >
        <Tab label="Planificados" />
        <Tab label="En Proceso" />
        <Tab label="Finalizados" />
      </Tabs>

      {/* Tabla */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Codigo</strong></TableCell>
              <TableCell><strong>Descripcion</strong></TableCell>
              <TableCell><strong>Fecha</strong></TableCell>
              <TableCell><strong>Origen</strong></TableCell>
              <TableCell><strong>Destino</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedManifiestos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="subtitle1" color="text.secondary">
                    No se encontraron manifiestos para esta selección.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedManifiestos.map((manifiesto) => (
                <TableRow key={manifiesto.manid}>
                  <TableCell>
                    <b>MN-{String(manifiesto.manid).padStart(6, "0")}</b>
                  </TableCell>
                  <TableCell>{manifiesto.maninf}</TableCell>
                  <TableCell>{formatFechaLarga(manifiesto.manfec)}</TableCell>
                  <TableCell>{manifiesto.manori}</TableCell>
                  <TableCell>{manifiesto.mandes}</TableCell>
                  <TableCell>
                    <Chip
                      label={estadoColorMap[manifiesto.manest]?.label || manifiesto.manest}
                      style={{
                        backgroundColor: estadoColorMap[manifiesto.manest]?.color || '#ccc',
                        color: estadoColorMap[manifiesto.manest]?.text || '#000',
                        fontWeight: 'bold'
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Detalles del Manifiesto">
                      <IconButton onClick={() => navigate(`/invitado/viaje/${manifiesto.manid}/pasajeros`)}>
                        <VisibilityIcon sx={{ color: "#009688" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ver Mapa de Ruta">
                      <IconButton size="small" onClick={() => {
                        //setOpenMapID(manifiesto.manid);
                        setMapData({
                          id: manifiesto.manid,
                          vehicleId: manifiesto.vehid
                        });
                        setOpenMap(true);
                      }}>
                        <PublicIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      <TablePagination
        component="div"
        count={filteredManifiestos.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Dialog
        open={openMap}        
        onClose={() => {
          setOpenMap(false)
          setOpenMapID("")
        }}
        fullWidth
        maxWidth="lg"
        sx={{ '& .MuiDialog-paper': { height: '80vh' } }}
      >
        <DialogTitle>Mapa de Ruta</DialogTitle>
        <DialogContent>
          {mapData && (
            <RouteMap route={mapData.id} vehid={mapData.vehicleId} />
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default ManifestClienteDashboard;
